import { isEmptyOrSpace } from '../other/ui/string-helper-functions';
import { noSqlDb } from '../database/no-sql-db';
import { sharedExecute } from '../other/common/helpers/callbacks';

export interface IPersistentApi<TInput, TResult> {
  run: (input: TInput) => Promise<TResult | null>;
  runSync: (input: TInput) => TResult | null;
  flushCache: (input?: TInput, forceGroup?: string) => Promise<void>;
}

interface PersistentApiState {
  keys: string[]; // persisted as array; internally we dedupe with a Set
  version: string;
  /** kvKey -> group, for caches built with `groupOf`. A key with no entry was stored before groups existed. */
  groups?: Record<string, string>;
}

interface PersistenceExecutionPlan<TResult> {
  execute: () => Promise<{ old: TResult | null; new: TResult | null }>;
  value?: TResult | null;
}
export function safeJsonHash(value: unknown): string {
  return djb2Hash(canonicalStringify(value));
}
/** -------- Utilities: canonical stringify + short hash for stable, compact keys -------- */

function canonicalStringify(value: unknown): string {
  // Keeps arrays ordered; sorts object keys to make stringification stable across key orders.
  const seen = new WeakSet<object>();
  const stringify = (v: any): string => {
    if (v === undefined) return '"[Undefined]"';
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (seen.has(v)) return '"[Circular]"';
    seen.add(v);

    if (Array.isArray(v)) {
      const arr = v.map(x => JSON.parse(stringify(x)));
      return JSON.stringify(arr);
    }

    // plain object
    const keys = Object.keys(v).sort();
    const obj: Record<string, unknown> = {};
    for (const k of keys) {
      obj[k] = JSON.parse(stringify(v[k]));
    }
    return JSON.stringify(obj);
  };
  return stringify(value);
}

export interface PersistentApiOptions<TInput, TResult> {
  getVersion?: () => Promise<string>;
  apiFunction: (input: TInput) => Promise<TResult | null>;
  flushEventTrigger?: (flushEvent: (input?: TInput, forceGroup?: string) => Promise<void>) => Promise<void>;
  afterUpdateEvent?: (oldValue: TResult | null, newValue: TResult | null) => Promise<void>;
  cacheKey: string;
  expiryTime?: number; // seconds; default 7 days
  overwriteOnVersionMismatch?: boolean;
  allowNullResults?: boolean;
  /**
   * The group an entry belongs to (for example its supplier). Stored with the entry's key, because the key is a
   * hash of the input, so a forced flush can drop one group's entries and keep the rest.
   */
  groupOf?: (input: TInput) => string;
}

function djb2Hash(str: string): string {
  // Simple stable hash; fast and dependency-free. Not cryptographic.
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // hash * 33 + char
    // eslint-disable-next-line no-bitwise -- djb2 is a bitwise hash by definition
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit and hex
  // eslint-disable-next-line no-bitwise -- the unsigned shift is how a 32-bit value is made unsigned
  return (hash >>> 0).toString(16);
}

function buildParamId(input: unknown): string {
  const canonical = canonicalStringify(input);
  // Param ID format keeps a short hash + length to avoid pathological collisions,
  // but still enables basic debugging.
  return `${djb2Hash(canonical)}_${canonical.length}`;
}

function toKvKey(cacheKey: string, paramId: string): string {
  return `${cacheKey}:param:${paramId}`;
}

/** -------------------------------- Implementation -------------------------------- */

const DEBUG_LOG_ENABLED = false;
function debugLog(...args: any[]) {
  if (DEBUG_LOG_ENABLED) {
    console.debug(...args);
  }
}

class PersistentApi<TInput, TResult> implements IPersistentApi<TInput, TResult> {
  state: PersistentApiState = {
    keys: [],
    version: ''
  };

  /** In-memory execution plans keyed by paramId (NOT the full kv key). */
  private executers: Map<string, PersistenceExecutionPlan<TResult>> = new Map();

  /** We use a Set for de-duplication internally; persisted as array. */
  private get stateKeySet(): Set<string> {
    return new Set(this.state.keys);
  }
  private setStateKeysFromSet(s: Set<string>) {
    this.state.keys = Array.from(s);
  }

  private initialized = false;

  private options: PersistentApiOptions<TInput, TResult>;

  private triggerRegistered = false;

  /** A version-guarded flush arrived before the state was loaded; init runs it once the state is in. */
  private versionCheckPending = false;

  /** One set per group flush running: the keys stored meanwhile, which it must keep tracking. */
  private readonly storedDuringFlushes = new Set<Set<string>>();

  constructor(options: PersistentApiOptions<TInput, TResult>) {
    this.options = options;
    // A grouped cache listens from the start, so a forced group flush also reaches entries stored in an earlier
    // page session that this page has not read yet. Caches without groups still register on first use.
    if (options.groupOf && options.flushEventTrigger) {
      this.triggerRegistered = true;
      void options.flushEventTrigger(this.flushCache);
    }
  }

  /** Build a fresh state with current version. */
  async blankState(): Promise<PersistentApiState> {
    return {
      keys: [],
      version: this.options.getVersion ? await this.options.getVersion() : ''
    };
  }

  /** Persist current state (optionally clearing/re-initializing it first). */
  async saveState(clearState = false) {
    if (clearState) {
      this.state = await this.blankState();
    }
    await noSqlDb.grab(
      `${this.options.cacheKey}:state`,
      () => Promise.resolve(this.state),
      this.options.expiryTime ?? 604800, // default 7 days
      undefined,
      true
    );
  }

  /** Load or initialize the persisted state. */
  async loadState() {
    const blankState = await this.blankState();
    this.state =
      (await noSqlDb.grab<PersistentApiState>(
        `${this.options.cacheKey}:state`,
        () => Promise.resolve(blankState),
        this.options.expiryTime ?? 604800
      )) || blankState;
  }

  /** One-time initializer guarded by sharedExecute to dedupe concurrent callers. */
  private init = sharedExecute(async () => {
    if (this.initialized) return;
    const startTime = performance.now();
    try {
      await this.loadState();
      if (this.options.flushEventTrigger && !this.triggerRegistered) {
        this.triggerRegistered = true;
        await this.options.flushEventTrigger(this.flushCache);
      }
      if (this.versionCheckPending) {
        this.versionCheckPending = false;
        await this.flushIfVersionChanged();
      }
    } finally {
      this.initialized = true;
      const duration = performance.now() - startTime;
      debugLog(`PersistentApi init [${this.options.cacheKey}]: ${duration.toFixed(2)}ms`);
    }
  });

  /**
   * Core flush logic:
   * - If `input` provided: unconditionally flush that input's KV + in-memory value & remove from state.
   * - Else: full flush when called, resetting state + in-memory executers.
   */
  private async internalFlushCache(input?: TInput) {
    const startTime = performance.now();
    const isTargeted = input !== undefined;

    try {
      if (isTargeted) {
        const paramId = buildParamId(input);
        const kvKey = toKvKey(this.options.cacheKey, paramId);

        await noSqlDb.delGrabItem(kvKey);

        const plan = this.executers.get(paramId);
        if (plan) plan.value = undefined;

        const set = this.stateKeySet;
        if (set.delete(kvKey)) {
          this.setStateKeysFromSet(set);
          await this.saveState();
        }
        return;
      }

      // Full flush: drop state record and all param entries
      await noSqlDb.delGrabItem(`${this.options.cacheKey}:state`);
      for (const kvKey of this.state.keys) {
        await noSqlDb.delGrabItem(kvKey);
      }

      // Clear in-memory cache values
      this.executers.forEach(plan => {
        plan.value = undefined;
      });

      // Reset state with fresh version
      this.state = await this.blankState();
      await this.saveState();
    } catch (error) {
      console.error('internalFlushCache failed:', error);
    } finally {
      const duration = performance.now() - startTime;
      debugLog(
        `PersistentApi flush [${this.options.cacheKey}]: ${duration.toFixed(2)}ms` +
          (isTargeted ? ` (targeted: ${buildParamId(input)})` : ' (full)')
      );
    }
  }

  /**
   * Drops one group's entries whatever the version, keeping every other group's. An entry stored before groups
   * existed cannot be attributed, so it is dropped too: better to flush more than to serve stale data.
   */
  private async flushGroup(group: string) {
    const prefix = toKvKey(this.options.cacheKey, '');
    const dropped = this.state.keys.filter(kvKey => {
      const owner = this.state.groups?.[kvKey];
      return owner === undefined || owner === group;
    });
    const storedMeanwhile = new Set<string>();
    this.storedDuringFlushes.add(storedMeanwhile);
    try {
      for (const kvKey of dropped) {
        await noSqlDb.delGrabItem(kvKey);
        const plan = this.executers.get(kvKey.slice(prefix.length));
        if (plan) plan.value = undefined;
      }
      // From the state as it is now: an entry stored while this ran, new or one it dropped, is kept, and still
      // found by the next flush.
      const droppedSet = new Set(dropped.filter(kvKey => !storedMeanwhile.has(kvKey)));
      const groups = { ...this.state.groups };
      droppedSet.forEach(kvKey => delete groups[kvKey]);
      this.state.keys = this.state.keys.filter(kvKey => !droppedSet.has(kvKey));
      this.state.groups = groups;
      await this.saveState();
    } catch (error) {
      console.error('flushGroup failed:', error);
    } finally {
      this.storedDuringFlushes.delete(storedMeanwhile);
    }
  }

  /**
   * Public flush API:
   * - If `input` is provided, flush that input unconditionally (expected behavior).
   * - If no input, perform a version-guarded full flush: only when version changed or is empty.
   * - With `forceGroup`, flush that group whatever the version (a full flush for a cache without groups): the
   *   server has said the data changed under it.
   */
  public flushCache = async (input?: TInput, forceGroup?: string) => {
    if (input !== undefined) {
      await this.internalFlushCache(input);
      return;
    }
    if (forceGroup !== undefined) {
      if (!this.initialized) await this.init(); // the stored state lists the entries to drop
      if (this.options.groupOf) await this.flushGroup(forceGroup);
      else await this.internalFlushCache();
      return;
    }

    // Before the cache has loaded its state there is nothing to compare, and an empty state would read as "version
    // changed" and wipe what earlier pages stored. The check is left for init to run once the state is loaded. Not
    // awaited here: init reads the version, which can reload the suppliers, which calls this flush again.
    if (!this.initialized) {
      this.versionCheckPending = true;
      return;
    }
    await this.flushIfVersionChanged();
  };

  /** Full flush when the stored version differs from the current one, or none was stored. */
  private async flushIfVersionChanged() {
    const version = this.state.version;
    const currentVersion = this.options.getVersion ? await this.options.getVersion() : '';
    if (version !== currentVersion || isEmptyOrSpace(version)) {
      await this.internalFlushCache(); // full flush
    }
  }

  /**
   * Run the API call with caching/locking:
   * - Ensures initialized.
   * - Builds/looks up an executer per paramId.
   * - Optionally triggers a full flush if overwriteOnVersionMismatch is set and version changed.
   * - Uses grabOldAndNew to atomically compute/update the value.
   */
  async run(input: TInput): Promise<TResult | null> {
    const startTime = performance.now();
    let cacheHit = false;
    let executionTime = 0;

    if (!this.initialized) await this.init();

    const paramId = buildParamId(input);
    let executer = this.executers.get(paramId);
    if (!executer) {
      const execute = this.createExecuter(paramId, input);
      executer = { execute, value: undefined };
      this.executers.set(paramId, executer);
    }

    // Optional global reset on version change (kept for feature parity).
    if (this.options.overwriteOnVersionMismatch) {
      const currentVersion = this.options.getVersion ? await this.options.getVersion() : '';
      if (this.state.version !== currentVersion) {
        await this.internalFlushCache(); // full reset
      }
    }

    if (executer.value !== undefined) {
      cacheHit = true;
      const endTime = performance.now();
      debugLog(`PersistentApi cache hit [${this.options.cacheKey}:${paramId}]: ${endTime - startTime}ms`);
      return executer.value;
    }

    try {
      const executeStartTime = performance.now();
      const values = await executer.execute();
      executionTime = performance.now() - executeStartTime;

      executer.value = this.options.allowNullResults ? values.new : (values.new ?? undefined);

      if (this.options.afterUpdateEvent) {
        try {
          await this.options.afterUpdateEvent(values.old, values.new);
        } catch (evtErr) {
          console.error('afterUpdateEvent failed:', evtErr);
          // Do not unset the cached value if the side-effect hook fails.
        }
      }
    } catch (error) {
      executer.value = undefined;
      console.error('Error executing PersistentApi:', error);
      throw error;
    } finally {
      const endTime = performance.now();
      const totalTime = endTime - startTime;

      debugLog(
        `PersistentApi ${cacheHit ? 'hit' : 'miss'} [${this.options.cacheKey}:${paramId}]: ${totalTime.toFixed(2)}ms` +
          (cacheHit ? '' : ` (API execution: ${executionTime.toFixed(2)}ms)`)
      );
    }

    return executer.value ?? null;
  }

  /**
   * Synchronous read of the last computed value for a given input.
   * Requires that run() has been called for that input previously.
   */
  runSync(input: TInput): TResult | null {
    if (!this.initialized) throw new Error('PersistentApi not initialized. Call run() first.');
    const paramId = buildParamId(input);
    const executer = this.executers.get(paramId);
    if (!executer) {
      throw new Error(`PersistentApi not initialized for input: ${paramId}`);
    }
    return executer.value ?? null;
  }

  /**
   * Creates a single-flight executer for a specific paramId.
   * Registers the KV key in state (de-duplicated) and relies on grabOldAndNew
   * to handle atomic compute/update and return { old, new }.
   */
  private createExecuter(paramId: string, input: TInput): () => Promise<{ old: TResult | null; new: TResult | null }> {
    return sharedExecute(async () => {
      const startTime = performance.now();
      const kvKey = toKvKey(this.options.cacheKey, paramId);

      // Record the kvKey in the state (deduped), with its group when the cache has groups
      this.storedDuringFlushes.forEach(stored => stored.add(kvKey));
      const set = this.stateKeySet;
      const group = this.options.groupOf?.(input);
      const groupMissing = group !== undefined && this.state.groups?.[kvKey] !== group;
      if (!set.has(kvKey) || groupMissing) {
        set.add(kvKey);
        this.setStateKeysFromSet(set);
        if (group !== undefined) this.state.groups = { ...this.state.groups, [kvKey]: group };
        await this.saveState();
      }

      const overwriteOnVersionMismatch =
        !!this.options.overwriteOnVersionMismatch && this.state.version !== ((await this.options.getVersion?.()) ?? '');

      const dbStartTime = performance.now();
      try {
        return await noSqlDb.grabOldAndNew<TResult>(
          kvKey,
          async () => {
            const apiFnStartTime = performance.now();
            try {
              return await this.options.apiFunction(input);
            } finally {
              const apiFnTime = performance.now() - apiFnStartTime;
              debugLog(`PersistentApi apiFunction [${this.options.cacheKey}:${paramId}]: ${apiFnTime.toFixed(2)}ms`);
            }
          },
          this.options.expiryTime ?? 604800,
          undefined,
          overwriteOnVersionMismatch
        );
      } finally {
        const dbTime = performance.now() - dbStartTime;
        const totalTime = performance.now() - startTime;
        debugLog(
          `PersistentApi executer [${this.options.cacheKey}:${paramId}]: total=${totalTime.toFixed(2)}ms, db=${dbTime.toFixed(2)}ms`
        );
      }
    });
  }
}

/** Builder */
export function buildPersistentApi<TInput, TResult>(
  options: PersistentApiOptions<TInput, TResult>
): IPersistentApi<TInput, TResult> {
  const api = new PersistentApi<TInput, TResult>(options);
  return api;
}
